/** Shareable group post/comment permalinks under /group/… (navigation stays /groups/…). */

const STREAM_PAGE_LIMIT = 10;

export function buildGroupPostUrl({ groupId, postId }) {
  return `/group/${groupId}/post/${postId}`;
}

export function buildGroupCommentUrl({ groupId, postId, commentId }) {
  return `/group/${groupId}/post/${postId}/comment/${commentId}`;
}

/** Hash-based deep link on /groups/… feeds (legacy + in-shell navigation). */
export function buildGroupFeedHashUrl({ groupId, threadId, page, postId }) {
  const base = threadId
    ? `/groups/${groupId}/threads/${threadId}`
    : `/groups/${groupId}`;
  const url = new URL(base, window.location.origin);
  if (page && page > 1) {
    url.searchParams.set('page', String(page));
  }
  url.hash = `post-${postId}`;
  return `${url.pathname}${url.search}${url.hash}`;
}

export function waitForElement(selector, root = document, timeout = 5000) {
  return new Promise((resolve) => {
    const find = () => {
      if (root !== document && typeof root.querySelector === 'function') {
        const scoped = root.querySelector(selector);
        if (scoped) {
          return scoped;
        }
      }
      return document.querySelector(selector);
    };

    const existing = find();
    if (existing) {
      resolve(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const element = find();
      if (element) {
        observer.disconnect();
        resolve(element);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(find());
    }, timeout);
  });
}

export async function waitForGroupShellFeedReady(timeout = 15000) {
  const start = Date.now();
  while (Date.now() - start < timeout) {
    const feed = document.querySelector('group-shell posts-feed');
    if (feed && !feed.isLoadingPosts && !feed.isLoading) {
      return feed;
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  return waitForElement('group-shell posts-feed', document, 5000);
}

async function waitForCommentsLoaded(commentsSection, timeout = 10000) {
  const start = Date.now();
  while (!commentsSection.commentsLoaded && Date.now() - start < timeout) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  while (commentsSection.isLoading && Date.now() - start < timeout) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
}

export async function fetchPostStreamPage(groupId, postId, { threadId = null } = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(STREAM_PAGE_LIMIT));
  if (threadId) {
    params.set('thread_id', String(threadId));
  }
  return window.API.request(
    `/groups/${groupId}/posts/${postId}/stream-page?${params}`
  );
}

export function highlightCommentElement(commentElement) {
  commentElement.classList.add('comment-highlight');
  setTimeout(() => {
    commentElement.classList.remove('comment-highlight');
  }, 3000);
}

async function waitForFeedIdle(feed, timeout = 10000) {
  const start = Date.now();
  while (feed && (feed.isLoadingPosts || feed.isLoading) && Date.now() - start < timeout) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
}

async function ensurePostVisibleInFeed(feed, postId, page, { expandComments = false } = {}) {
  if (!feed) {
    return null;
  }

  const targetPage = page || 1;
  if (feed.currentPage !== targetPage) {
    feed.currentPage = targetPage;
    await feed.load(true);
  } else {
    await waitForFeedIdle(feed);
  }

  const anchor = await waitForElement(`#post-${postId}`, feed, 10000);
  if (!anchor) {
    return null;
  }

  const postItem = anchor.closest('post-item');
  if (expandComments && postItem && typeof postItem.openComments === 'function') {
    postItem.openComments(true);
  }

  return { anchor, postItem };
}

/**
 * Focus a group post in the current shell feed (expand comments optional via openComments only if needed).
 * @param {{ groupId: number, postId: number, location?: object }} options
 */
export async function focusGroupPost({ groupId, postId, location = null }) {
  let streamLocation = location;
  if (!streamLocation && window.API) {
    try {
      streamLocation = await fetchPostStreamPage(groupId, postId);
    } catch (_err) {
      return false;
    }
  }

  const feed = await waitForElement('group-shell posts-feed');
  if (!feed) {
    return false;
  }

  const result = await ensurePostVisibleInFeed(feed, postId, streamLocation?.page);
  if (!result?.anchor) {
    return false;
  }

  result.anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
}

/**
 * Focus a group comment: load correct feed page, expand comments, scroll to comment.
 * @param {{ groupId: number, postId: number, commentId: number, location?: object }} options
 */
export async function focusGroupComment({ groupId, postId, commentId, location = null }) {
  let streamLocation = location;
  if (!streamLocation && window.API) {
    try {
      streamLocation = await fetchPostStreamPage(groupId, postId);
    } catch (_err) {
      return false;
    }
  }

  const feed = await waitForElement('group-shell posts-feed');
  if (!feed) {
    return false;
  }

  const result = await ensurePostVisibleInFeed(feed, postId, streamLocation?.page);
  if (!result?.postItem) {
    return false;
  }

  const commentsSection = await waitForElement('comments-section', result.postItem, 10000);
  if (!commentsSection) {
    return false;
  }

  if (!commentsSection.commentsLoaded) {
    await commentsSection.loadComments();
  } else {
    await waitForCommentsLoaded(commentsSection);
  }

  if (result.postItem && typeof result.postItem.openComments === 'function') {
    result.postItem.openComments(true);
  }

  const commentElement = await waitForElement(
    `[data-comment-id="${commentId}"]`,
    result.postItem,
    10000
  );
  if (!commentElement) {
    window.logger?.warn(`Group comment ${commentId} not found in post ${postId}`);
    return false;
  }

  commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  highlightCommentElement(commentElement);
  return true;
}

/**
 * Resolve stream page for hash deep links; reload feed or navigate when thread/page differs.
 */
export async function syncFeedToPostStream({ postId, feed, pathContext }) {
  if (!pathContext?.groupId || !window.API) {
    return false;
  }

  let location;
  try {
    location = await fetchPostStreamPage(pathContext.groupId, postId, {
      threadId: pathContext.threadId
    });
  } catch (_err) {
    return false;
  }

  const resolvedThreadId = location.threadId ?? null;
  const pathThreadId = pathContext.threadId ?? null;

  if (resolvedThreadId !== pathThreadId && window.router) {
    window.router.navigate(buildGroupFeedHashUrl({
      groupId: location.groupId,
      threadId: resolvedThreadId,
      page: location.page,
      postId
    }));
    return true;
  }

  if (!feed) {
    return false;
  }

  const targetPage = location.page || 1;
  if (feed.currentPage !== targetPage) {
    feed.currentPage = targetPage;
    feed._hashFocusRetry = true;
    await feed.load(true);
    return true;
  }

  return false;
}
